import { observable, action, reaction, computed } from 'mobx';
import { fromPromise, IPromiseBasedObservable, FULFILLED } from 'mobx-utils';
import jwtDecode from 'jwt-decode';
import { RootStore } from '../stores/index';
import * as authenticationApi from '../api/authenticationApi';
import Account, { IAccount } from '../models/Account';

export interface JwtPayload {
  sub : number,
  role : string
};

export interface AccountCredentials {
  email : string,
  password : string
}

export interface NewAccountDetails {
  firstName : string,
  lastName : string,
  email : string,
  password : string
}

type ResponseDetails = {
  message : string
};

class AuthenticationStore {
  rootStore: RootStore;

  @observable accountDetailsRequest : IPromiseBasedObservable<IAccount> | null = null;
  @observable jwtPayload : JwtPayload | null = null;

  @observable createAccountMessage : string | null = null;
  @observable loginErrorMessage : string | null = null;

  constructor(rootStore : RootStore) {
    this.rootStore = rootStore;
    this.parseJwtPayloadFromCookie();
    
    reaction(
      () => this.jwtPayload,
      () => this.getAccountDetails(),
      { fireImmediately: true }
    );

    this.payloadHasRole = this.payloadHasRole.bind(this);
  }

  payloadHasRole(role : string) : boolean {
    if (this.jwtPayload === null)
      return false;

    return this.jwtPayload.role.includes(role);
  }

  @computed
  get account() : IAccount | null {
    const accountDetailsRequest = this.accountDetailsRequest;
    if (accountDetailsRequest === null)
      return null;

    const { state, value } = accountDetailsRequest;
    if (state !== FULFILLED || value === null)
      return null;

    return Account.create(value);  
  }

  @computed
  get authenticated() : boolean {
    return this.jwtPayload !== null;
  }

  @action
  parseJwtPayloadFromCookie() {
    const cookies = document.cookie.split(';');
    const accessToken = cookies.find(cookie => cookie.includes('access_token'));

    try {
      this.jwtPayload = jwtDecode<JwtPayload>(accessToken as string);
    } catch {
      this.jwtPayload = null;
    }
  }

  @action
  authenticateAccount(accountCredentials : AccountCredentials) {
    if (accountCredentials === null)
      return;

    authenticationApi.authenticateAccount(accountCredentials)
      .then(response => {
        if (response.ok) {
          this.parseJwtPayloadFromCookie();
          return;
        }
        return response.json();
      })
      .then(action((errorDetails : ResponseDetails | undefined) => {
        if (errorDetails === undefined)
          return;

        this.loginErrorMessage = errorDetails.message;
      }))
      .catch(console.error);
  }

  @action
  addAccount(newAccountDetails : NewAccountDetails) {
    if (newAccountDetails === null)
      return;

    authenticationApi.addAccount(newAccountDetails)
      .then(response => {
        if (response.ok) {
          this.createAccountMessage = 'Account Created Successfully';
          return;
        }
        return response.json();
      })
      .then(action((errorDetails : ResponseDetails | undefined) => {
        if (errorDetails === undefined)
          return;

        this.createAccountMessage = errorDetails.message;
      }))
      .catch(console.error);
  }

  @action
  getAccountDetails() {
    if (this.jwtPayload === null)
      return;

    this.accountDetailsRequest = fromPromise(authenticationApi.getAccountDetails());
  }
}

export default AuthenticationStore;